import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Shield,
  Mail,
  Phone,
  Globe,
  Link2,
  Eye,
  Search,
  AlertTriangle,
  CheckCircle,
  Lock,
  Zap,
  Star,
  Crown,
  ArrowRight,
  Fingerprint,
  MapPin,
  Wifi,
  Server,
  FileText,
  Users,
  TrendingUp,
  BookOpen,
  Target,
  Lightbulb,
  Code,
  Copy,
  ExternalLink,
  Clock,
  DollarSign,
  Check,
  X
} from "lucide-react";
import { toast } from "sonner";

const InvestigatorsGuidePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [copiedCode, setCopiedCode] = useState(null);

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(id);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const subscriptionPlans = [
    {
      name: "Free",
      price: "$0",
      period: "forever",
      color: "border-muted",
      features: {
        email_lookups: 2,
        phone_lookups: 2,
        social_lookups: 0,
        ip_lookups: 2,
        vpn_detection: "Basic (~30%)",
        email_headers: true,
        tracking_pixel: true,
        link_tracking: true,
        export_csv: false,
        api_access: false,
        priority_support: false
      },
      best_for: "Trying out the platform"
    },
    {
      name: "Scam Fighter",
      price: "$9.99",
      period: "/month",
      color: "border-blue-500",
      highlight: false,
      features: {
        email_lookups: 20,
        phone_lookups: 20,
        social_lookups: 20,
        ip_lookups: 50,
        vpn_detection: "Enhanced (95%+)",
        email_headers: true,
        tracking_pixel: true,
        link_tracking: true,
        export_csv: true,
        api_access: false,
        priority_support: false
      },
      best_for: "Individual protection"
    },
    {
      name: "Scam Hunter",
      price: "$24.99",
      period: "/month",
      color: "border-purple-500",
      highlight: true,
      features: {
        email_lookups: 100,
        phone_lookups: 100,
        social_lookups: 100,
        ip_lookups: 500,
        vpn_detection: "Enhanced (95%+)",
        email_headers: true,
        tracking_pixel: true,
        link_tracking: true,
        export_csv: true,
        api_access: true,
        priority_support: false
      },
      best_for: "Small business & freelancers"
    },
    {
      name: "Professional",
      price: "$49.99",
      period: "/month",
      color: "border-amber-500",
      features: {
        email_lookups: 500,
        phone_lookups: 500,
        social_lookups: 500,
        ip_lookups: 2500,
        vpn_detection: "Enhanced (95%+)",
        email_headers: true,
        tracking_pixel: true,
        link_tracking: true,
        export_csv: true,
        api_access: true,
        priority_support: true
      },
      best_for: "Agencies & high-volume users"
    }
  ];

  return (
    <div className="space-y-8" data-testid="investigators-guide-page">
      {/* Hero Section */}
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-primary/20 via-primary/10 to-background border border-primary/20 p-8">
        <div className="absolute top-0 right-0 w-64 h-64 bg-primary/10 rounded-full blur-3xl" />
        <div className="relative">
          <Badge className="bg-primary/20 text-primary mb-4">
            <BookOpen className="w-3 h-3 mr-1" />
            Complete Investigator's Guide
          </Badge>
          <h1 className="text-3xl md:text-4xl font-mono font-bold tracking-tight mb-4">
            How to Use ScamFinder
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mb-6">
            Your step-by-step guide to investigating suspicious emails, phone numbers, and online scammers. Learn how to protect yourself and uncover the truth.
          </p>
          <div className="flex flex-wrap gap-3">
            <Button onClick={() => navigate('/investigate')} className="gap-2">
              <Search className="w-4 h-4" />
              Start Investigating
            </Button>
            <Button variant="outline" onClick={() => navigate('/subscription')} className="gap-2">
              View Plans
              <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Table of Contents */}
      <Card className="bg-card/50 border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" />
            What You'll Learn
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            <a href="#email-investigation" className="p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
              <Mail className="w-5 h-5 text-primary mb-2" />
              <p className="font-medium">Email Investigation</p>
              <p className="text-xs text-muted-foreground">Verify emails, detect scams</p>
            </a>
            <a href="#phone-investigation" className="p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
              <Phone className="w-5 h-5 text-emerald-500 mb-2" />
              <p className="font-medium">Phone Number Lookup</p>
              <p className="text-xs text-muted-foreground">Identify carriers, detect VoIP</p>
            </a>
            <a href="#tracking-tools" className="p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
              <Eye className="w-5 h-5 text-purple-500 mb-2" />
              <p className="font-medium">Tracking Tools</p>
              <p className="text-xs text-muted-foreground">Pixels, links, email tracking</p>
            </a>
            <a href="#email-headers" className="p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
              <Code className="w-5 h-5 text-amber-500 mb-2" />
              <p className="font-medium">Email Header Analysis</p>
              <p className="text-xs text-muted-foreground">Trace sender's real IP</p>
            </a>
            <a href="#ip-logging" className="p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
              <Globe className="w-5 h-5 text-blue-500 mb-2" />
              <p className="font-medium">IP Logging</p>
              <p className="text-xs text-muted-foreground">Track visitors, detect VPNs</p>
            </a>
            <a href="#pricing" className="p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
              <DollarSign className="w-5 h-5 text-green-500 mb-2" />
              <p className="font-medium">Plans & Pricing</p>
              <p className="text-xs text-muted-foreground">Feature comparison</p>
            </a>
          </div>
        </CardContent>
      </Card>

      {/* Section 1: Email Investigation */}
      <Card id="email-investigation" className="bg-card/50 border-border scroll-mt-4">
        <CardHeader className="bg-primary/5 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
              <Mail className="w-6 h-6 text-primary" />
            </div>
            <div>
              <CardTitle className="text-xl">Email Investigation</CardTitle>
              <p className="text-sm text-muted-foreground">Verify suspicious emails and expose scammers</p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-6 space-y-6">
          <div className="prose prose-invert max-w-none">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Target className="w-5 h-5 text-primary" />
              When to Use Email Investigation
            </h3>
            <ul className="space-y-2 text-sm text-muted-foreground mt-3">
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                <span>Received an email from someone claiming to be from a company, government, or bank</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                <span>Got an "urgent" email asking you to click a link or send money</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                <span>Want to verify if a job offer, romantic interest, or seller is legitimate</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                <span>Need to check if an email address is real before sending important information</span>
              </li>
            </ul>
          </div>

          <Separator />

          <div>
            <h3 className="text-lg font-semibold flex items-center gap-2 mb-4">
              <Lightbulb className="w-5 h-5 text-amber-500" />
              Step-by-Step Guide
            </h3>
            <div className="space-y-4">
              <div className="p-4 rounded-lg bg-muted/30 border-l-4 border-primary">
                <p className="font-medium">Step 1: Go to the Investigate Page</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Click "Investigate" in the sidebar menu, or use the Quick Actions widget on your Dashboard.
                </p>
              </div>
              <div className="p-4 rounded-lg bg-muted/30 border-l-4 border-primary">
                <p className="font-medium">Step 2: Enter the Email Address</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Type or paste the suspicious email address in the "Email" tab. For example: <code className="bg-background px-1 rounded">suspicious@domain.com</code>
                </p>
              </div>
              <div className="p-4 rounded-lg bg-muted/30 border-l-4 border-primary">
                <p className="font-medium">Step 3: Review the Results</p>
                <p className="text-sm text-muted-foreground mt-1">
                  ScamFinder will analyze the email and show you:
                </p>
                <ul className="mt-2 space-y-1 text-sm">
                  <li>• <strong>Risk Score (0-100%)</strong> - Higher = more suspicious</li>
                  <li>• <strong>Deliverable</strong> - Does this mailbox actually exist?</li>
                  <li>• <strong>Disposable</strong> - Is it a temporary/throwaway email?</li>
                  <li>• <strong>Free Provider</strong> - Gmail, Yahoo, etc. (not necessarily bad)</li>
                  <li>• <strong>Domain Reputation</strong> - Known for spam or abuse?</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/20">
            <h4 className="font-semibold text-red-500 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              Red Flags to Watch For
            </h4>
            <ul className="mt-2 space-y-1 text-sm">
              <li>• Risk score above 50% - Proceed with caution</li>
              <li>• Disposable email detected - Likely hiding their identity</li>
              <li>• Email doesn't exist (not deliverable) - Probably spoofed</li>
              <li>• Domain is very new or has bad reputation</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Section 2: Phone Investigation */}
      <Card id="phone-investigation" className="bg-card/50 border-border scroll-mt-4">
        <CardHeader className="bg-emerald-500/5 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-emerald-500/10 flex items-center justify-center">
              <Phone className="w-6 h-6 text-emerald-500" />
            </div>
            <div>
              <CardTitle className="text-xl">Phone Number Lookup</CardTitle>
              <p className="text-sm text-muted-foreground">Identify carriers, detect VoIP, and verify callers</p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-6 space-y-6">
          <div className="prose prose-invert max-w-none">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Target className="w-5 h-5 text-emerald-500" />
              When to Use Phone Lookup
            </h3>
            <ul className="space-y-2 text-sm text-muted-foreground mt-3">
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                <span>Received a call from an unknown number claiming to be IRS, Social Security, or tech support</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                <span>Someone is pressuring you to call them back at a specific number</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                <span>Want to verify if a phone number belongs to a real business</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                <span>Checking if someone gave you a real phone number</span>
              </li>
            </ul>
          </div>

          <Separator />

          <div>
            <h3 className="text-lg font-semibold flex items-center gap-2 mb-4">
              <Lightbulb className="w-5 h-5 text-amber-500" />
              Step-by-Step Guide
            </h3>
            <div className="space-y-4">
              <div className="p-4 rounded-lg bg-muted/30 border-l-4 border-emerald-500">
                <p className="font-medium">Step 1: Go to the Investigate Page</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Click "Investigate" in the sidebar, then select the "Phone" tab.
                </p>
              </div>
              <div className="p-4 rounded-lg bg-muted/30 border-l-4 border-emerald-500">
                <p className="font-medium">Step 2: Enter the Phone Number</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Enter the full phone number including country code. For US numbers: <code className="bg-background px-1 rounded">+1 555 123 4567</code>
                </p>
              </div>
              <div className="p-4 rounded-lg bg-muted/30 border-l-4 border-emerald-500">
                <p className="font-medium">Step 3: Analyze the Results</p>
                <p className="text-sm text-muted-foreground mt-1">
                  ScamFinder will reveal:
                </p>
                <ul className="mt-2 space-y-1 text-sm">
                  <li>• <strong>Carrier</strong> - AT&T, Verizon, T-Mobile, etc.</li>
                  <li>• <strong>Line Type</strong> - Mobile, Landline, or VoIP</li>
                  <li>• <strong>VoIP Flag</strong> - Is it Google Voice, Skype, or a burner app?</li>
                  <li>• <strong>Location</strong> - State and city based on area code</li>
                  <li>• <strong>Risk Score</strong> - How suspicious is this number?</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/20">
            <h4 className="font-semibold text-red-500 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              Red Flags to Watch For
            </h4>
            <ul className="mt-2 space-y-1 text-sm">
              <li>• <strong>VoIP detected</strong> - Scammers often use Google Voice, TextNow, or other VoIP services because they're hard to trace</li>
              <li>• <strong>Location doesn't match claim</strong> - Caller says they're from your local bank but number is from another state</li>
              <li>• <strong>Risk score above 50%</strong> - Multiple fraud indicators detected</li>
              <li>• <strong>"Landline" type for mobile-only services</strong> - May be spoofed</li>
            </ul>
          </div>

          <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
            <h4 className="font-semibold text-amber-500 flex items-center gap-2">
              <Lightbulb className="w-4 h-4" />
              Pro Tip: VoIP Numbers
            </h4>
            <p className="text-sm mt-2">
              VoIP numbers aren't always bad - many legitimate businesses use them. However, scammers LOVE VoIP because:
            </p>
            <ul className="mt-2 space-y-1 text-sm">
              <li>• They can get multiple numbers instantly for free</li>
              <li>• Numbers can be disposed of and changed quickly</li>
              <li>• They can make the number appear to be from any area code</li>
              <li>• VoIP numbers are harder for law enforcement to trace</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Section 3: Tracking Tools */}
      <Card id="tracking-tools" className="bg-card/50 border-border scroll-mt-4">
        <CardHeader className="bg-purple-500/5 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-purple-500/10 flex items-center justify-center">
              <Eye className="w-6 h-6 text-purple-500" />
            </div>
            <div>
              <CardTitle className="text-xl">Tracking Tools</CardTitle>
              <p className="text-sm text-muted-foreground">Capture IPs using tracking pixels and links</p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-6 space-y-6">
          <div className="prose prose-invert max-w-none">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Target className="w-5 h-5 text-purple-500" />
              What Are Tracking Tools?
            </h3>
            <p className="text-sm text-muted-foreground mt-2">
              Tracking tools let you capture someone's IP address when they view an image or click a link. This is useful for:
            </p>
            <ul className="space-y-2 text-sm text-muted-foreground mt-3">
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" />
                <span>Confirming if someone opened your email (even if they didn't reply)</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" />
                <span>Identifying the location of someone who clicked your link</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" />
                <span>Gathering evidence for harassment or fraud cases</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" />
                <span>Monitoring who visits your website or views your content</span>
              </li>
            </ul>
          </div>

          <Separator />

          <div>
            <h3 className="text-lg font-semibold flex items-center gap-2 mb-4">
              <Link2 className="w-5 h-5 text-purple-500" />
              Link Tracker (Recommended)
            </h3>
            <div className="space-y-4">
              <div className="p-4 rounded-lg bg-muted/30 border-l-4 border-purple-500">
                <p className="font-medium">Step 1: Go to Link Tracker</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Click "Link Tracker" in the sidebar menu.
                </p>
              </div>
              <div className="p-4 rounded-lg bg-muted/30 border-l-4 border-purple-500">
                <p className="font-medium">Step 2: Create a Tracked Link</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Enter any URL you want to share (like a document, website, or image). ScamFinder will create a special tracking link.
                </p>
              </div>
              <div className="p-4 rounded-lg bg-muted/30 border-l-4 border-purple-500">
                <p className="font-medium">Step 3: Share the Link</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Send the tracking link via email, text, or any messaging platform. When clicked, you'll capture:
                </p>
                <ul className="mt-2 space-y-1 text-sm">
                  <li>• IP address and location</li>
                  <li>• Browser and device type</li>
                  <li>• VPN/proxy detection</li>
                  <li>• Exact time of click</li>
                </ul>
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold flex items-center gap-2 mb-4">
              <Code className="w-5 h-5 text-amber-500" />
              Tracking Pixel (For Emails)
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              A tracking pixel is an invisible 1x1 image that logs IP addresses when loaded. Perfect for embedding in emails.
            </p>
            <div className="p-4 rounded-lg bg-muted/30">
              <p className="font-medium mb-2">How to Use:</p>
              <ol className="space-y-2 text-sm">
                <li>1. Go to "Tracking Pixel" in the sidebar</li>
                <li>2. Copy your unique pixel URL</li>
                <li>3. In your email, insert it as an image (most email clients support this)</li>
                <li>4. When the recipient opens the email and images load, their IP is logged</li>
              </ol>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Section 4: Email Header Analysis */}
      <Card id="email-headers" className="bg-card/50 border-border scroll-mt-4">
        <CardHeader className="bg-amber-500/5 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-amber-500/10 flex items-center justify-center">
              <Code className="w-6 h-6 text-amber-500" />
            </div>
            <div>
              <CardTitle className="text-xl">Email Header Analysis</CardTitle>
              <p className="text-sm text-muted-foreground">Trace the real origin of suspicious emails</p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-6 space-y-6">
          <div className="prose prose-invert max-w-none">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Target className="w-5 h-5 text-amber-500" />
              What Are Email Headers?
            </h3>
            <p className="text-sm text-muted-foreground mt-2">
              Email headers are hidden metadata that show the complete path an email took from sender to your inbox. 
              Even if a scammer spoofs the "From" address, the headers often reveal their real IP address.
            </p>
          </div>

          <Separator />

          <div>
            <h3 className="text-lg font-semibold flex items-center gap-2 mb-4">
              <Lightbulb className="w-5 h-5 text-amber-500" />
              How to Get Email Headers
            </h3>
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="gmail">
                <AccordionTrigger>Gmail</AccordionTrigger>
                <AccordionContent>
                  <ol className="space-y-2 text-sm">
                    <li>1. Open the email</li>
                    <li>2. Click the three dots (⋮) in the top right</li>
                    <li>3. Select "Show original"</li>
                    <li>4. Copy all the text from the new window</li>
                    <li>5. Paste into ScamFinder's Email Header Analyzer</li>
                  </ol>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="outlook">
                <AccordionTrigger>Outlook / Office 365</AccordionTrigger>
                <AccordionContent>
                  <ol className="space-y-2 text-sm">
                    <li>1. Open the email</li>
                    <li>2. Click File → Properties</li>
                    <li>3. Look for "Internet headers" at the bottom</li>
                    <li>4. Copy all the header text</li>
                    <li>5. Paste into ScamFinder's Email Header Analyzer</li>
                  </ol>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="yahoo">
                <AccordionTrigger>Yahoo Mail</AccordionTrigger>
                <AccordionContent>
                  <ol className="space-y-2 text-sm">
                    <li>1. Open the email</li>
                    <li>2. Click the three dots (⋯) next to Reply</li>
                    <li>3. Select "View raw message"</li>
                    <li>4. Copy all the text</li>
                    <li>5. Paste into ScamFinder's Email Header Analyzer</li>
                  </ol>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="apple">
                <AccordionTrigger>Apple Mail</AccordionTrigger>
                <AccordionContent>
                  <ol className="space-y-2 text-sm">
                    <li>1. Open the email</li>
                    <li>2. Go to View → Message → All Headers</li>
                    <li>3. Select all the header text</li>
                    <li>4. Copy and paste into ScamFinder</li>
                  </ol>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>

          <div className="p-4 rounded-lg bg-primary/10 border border-primary/20">
            <h4 className="font-semibold text-primary flex items-center gap-2">
              <Fingerprint className="w-4 h-4" />
              What ScamFinder Reveals
            </h4>
            <ul className="mt-2 space-y-1 text-sm">
              <li>• <strong>Sender's real IP address</strong> - Even if they spoofed the "From" field</li>
              <li>• <strong>Geographic location</strong> - Country, city, ISP of the sender</li>
              <li>• <strong>Mail server path</strong> - Every server the email passed through</li>
              <li>• <strong>Authentication results</strong> - SPF, DKIM, DMARC pass/fail</li>
              <li>• <strong>Suspicious indicators</strong> - Signs of spoofing or phishing</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Section 5: IP Logging */}
      <Card id="ip-logging" className="bg-card/50 border-border scroll-mt-4">
        <CardHeader className="bg-blue-500/5 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-blue-500/10 flex items-center justify-center">
              <Globe className="w-6 h-6 text-blue-500" />
            </div>
            <div>
              <CardTitle className="text-xl">IP Logging & VPN Detection</CardTitle>
              <p className="text-sm text-muted-foreground">Track visitors and identify hidden identities</p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-6 space-y-6">
          <div className="prose prose-invert max-w-none">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Target className="w-5 h-5 text-blue-500" />
              Understanding IP Logs
            </h3>
            <p className="text-sm text-muted-foreground mt-2">
              Every time someone visits your tracked links or pages, ScamFinder captures detailed information about their connection.
              This helps you identify who's actually behind the screen.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="p-4 rounded-lg bg-muted/30">
              <h4 className="font-semibold flex items-center gap-2 mb-3">
                <MapPin className="w-4 h-4 text-blue-500" />
                Location Data
              </h4>
              <ul className="space-y-1 text-sm text-muted-foreground">
                <li>• Country & Region</li>
                <li>• City</li>
                <li>• Timezone</li>
                <li>• ISP Name</li>
              </ul>
            </div>
            <div className="p-4 rounded-lg bg-muted/30">
              <h4 className="font-semibold flex items-center gap-2 mb-3">
                <Fingerprint className="w-4 h-4 text-purple-500" />
                Device Info
              </h4>
              <ul className="space-y-1 text-sm text-muted-foreground">
                <li>• Browser Type & Version</li>
                <li>• Operating System</li>
                <li>• Device Type (Mobile/Desktop)</li>
                <li>• Screen Resolution</li>
              </ul>
            </div>
          </div>

          <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
            <h4 className="font-semibold text-emerald-500 flex items-center gap-2">
              <Shield className="w-4 h-4" />
              VPN & Proxy Detection (95%+ Accuracy)
            </h4>
            <p className="text-sm mt-2">
              ScamFinder uses advanced detection to identify when someone is hiding behind a VPN, proxy, or Tor network.
              The enhanced detection (available in paid plans) can identify:
            </p>
            <ul className="mt-2 space-y-1 text-sm">
              <li>• Commercial VPN services (NordVPN, ExpressVPN, etc.)</li>
              <li>• Proxy servers and web proxies</li>
              <li>• Tor exit nodes</li>
              <li>• Datacenter IPs (often used for bots)</li>
              <li>• Residential proxies</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Section 6: Plans & Pricing Comparison */}
      <Card id="pricing" className="bg-card/50 border-border scroll-mt-4">
        <CardHeader className="bg-gradient-to-r from-primary/10 to-amber-500/10 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-primary" />
            </div>
            <div>
              <CardTitle className="text-xl">Plans & Pricing</CardTitle>
              <p className="text-sm text-muted-foreground">Compare features and choose the right plan for you</p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-6 space-y-6">
          {/* Plan Cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {subscriptionPlans.map((plan, index) => (
              <div 
                key={index} 
                className={`relative p-5 rounded-xl border-2 ${plan.color} ${plan.highlight ? 'ring-2 ring-purple-500 ring-offset-2 ring-offset-background' : ''}`}
              >
                {plan.highlight && (
                  <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-purple-500">
                    Most Popular
                  </Badge>
                )}
                <h3 className="font-bold text-lg">{plan.name}</h3>
                <div className="mt-2">
                  <span className="text-3xl font-bold">{plan.price}</span>
                  <span className="text-muted-foreground">{plan.period}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-2">{plan.best_for}</p>
                <Separator className="my-4" />
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-primary" />
                    <span>{plan.features.email_lookups} email lookups</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-emerald-500" />
                    <span>{plan.features.phone_lookups} phone lookups</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-purple-500" />
                    <span>{plan.features.social_lookups} social lookups</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Globe className="w-4 h-4 text-blue-500" />
                    <span>{plan.features.ip_lookups} IP lookups</span>
                  </li>
                </ul>
              </div>
            ))}
          </div>

          {/* Feature Comparison Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left p-3">Feature</th>
                  <th className="text-center p-3">Free</th>
                  <th className="text-center p-3 text-blue-500">Scam Fighter</th>
                  <th className="text-center p-3 text-purple-500">Scam Hunter</th>
                  <th className="text-center p-3 text-amber-500">Professional</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-border/50">
                  <td className="p-3">Enhanced VPN Detection (95%+)</td>
                  <td className="text-center p-3"><X className="w-4 h-4 mx-auto text-muted-foreground" /></td>
                  <td className="text-center p-3"><Check className="w-4 h-4 mx-auto text-emerald-500" /></td>
                  <td className="text-center p-3"><Check className="w-4 h-4 mx-auto text-emerald-500" /></td>
                  <td className="text-center p-3"><Check className="w-4 h-4 mx-auto text-emerald-500" /></td>
                </tr>
                <tr className="border-b border-border/50">
                  <td className="p-3">Social Profile Discovery</td>
                  <td className="text-center p-3"><X className="w-4 h-4 mx-auto text-muted-foreground" /></td>
                  <td className="text-center p-3"><Check className="w-4 h-4 mx-auto text-emerald-500" /></td>
                  <td className="text-center p-3"><Check className="w-4 h-4 mx-auto text-emerald-500" /></td>
                  <td className="text-center p-3"><Check className="w-4 h-4 mx-auto text-emerald-500" /></td>
                </tr>
                <tr className="border-b border-border/50">
                  <td className="p-3">CSV/JSON Export</td>
                  <td className="text-center p-3"><X className="w-4 h-4 mx-auto text-muted-foreground" /></td>
                  <td className="text-center p-3"><Check className="w-4 h-4 mx-auto text-emerald-500" /></td>
                  <td className="text-center p-3"><Check className="w-4 h-4 mx-auto text-emerald-500" /></td>
                  <td className="text-center p-3"><Check className="w-4 h-4 mx-auto text-emerald-500" /></td>
                </tr>
                <tr className="border-b border-border/50">
                  <td className="p-3">API Access</td>
                  <td className="text-center p-3"><X className="w-4 h-4 mx-auto text-muted-foreground" /></td>
                  <td className="text-center p-3"><X className="w-4 h-4 mx-auto text-muted-foreground" /></td>
                  <td className="text-center p-3"><Check className="w-4 h-4 mx-auto text-emerald-500" /></td>
                  <td className="text-center p-3"><Check className="w-4 h-4 mx-auto text-emerald-500" /></td>
                </tr>
                <tr className="border-b border-border/50">
                  <td className="p-3">Priority Support</td>
                  <td className="text-center p-3"><X className="w-4 h-4 mx-auto text-muted-foreground" /></td>
                  <td className="text-center p-3"><X className="w-4 h-4 mx-auto text-muted-foreground" /></td>
                  <td className="text-center p-3"><X className="w-4 h-4 mx-auto text-muted-foreground" /></td>
                  <td className="text-center p-3"><Check className="w-4 h-4 mx-auto text-emerald-500" /></td>
                </tr>
                <tr className="border-b border-border/50">
                  <td className="p-3">Email Header Analysis</td>
                  <td className="text-center p-3"><Check className="w-4 h-4 mx-auto text-emerald-500" /></td>
                  <td className="text-center p-3"><Check className="w-4 h-4 mx-auto text-emerald-500" /></td>
                  <td className="text-center p-3"><Check className="w-4 h-4 mx-auto text-emerald-500" /></td>
                  <td className="text-center p-3"><Check className="w-4 h-4 mx-auto text-emerald-500" /></td>
                </tr>
                <tr className="border-b border-border/50">
                  <td className="p-3">Tracking Pixel & Links</td>
                  <td className="text-center p-3"><Check className="w-4 h-4 mx-auto text-emerald-500" /></td>
                  <td className="text-center p-3"><Check className="w-4 h-4 mx-auto text-emerald-500" /></td>
                  <td className="text-center p-3"><Check className="w-4 h-4 mx-auto text-emerald-500" /></td>
                  <td className="text-center p-3"><Check className="w-4 h-4 mx-auto text-emerald-500" /></td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="text-center pt-4">
            <Button size="lg" onClick={() => navigate('/subscription')} className="gap-2">
              <Crown className="w-5 h-5" />
              Upgrade Your Plan
            </Button>
            <p className="text-xs text-muted-foreground mt-3">
              Or try our 7-day free trial with 5 lookups each. No credit card required.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default InvestigatorsGuidePage;
